<script lang="ts" setup>
import type { DesktopMenuItem } from '~/types/SiteSettings';

const desktopMenu: Ref<DesktopMenuItem[]> = ref([]);

const siteStore = useSiteStore();
desktopMenu.value = await siteStore.getDesktopMenu();

const hoveredItem = ref<DesktopMenuItem | null>(null);
const menuWrapperRef = ref<HTMLElement | null>(null);
const dropdownRef = ref<HTMLElement | null>(null);
const dropdownLeft = ref(0);
const dropdownMinWidth = ref(0);

const isDropDownOpen = computed(() => hoveredItem.value?.children?.length);

async function openDropDown(item: DesktopMenuItem, event: MouseEvent) {
  hoveredItem.value = item;
  const wrapperRect = menuWrapperRef.value?.getBoundingClientRect();
  const target = event.currentTarget as HTMLElement | null;

  if (wrapperRect && target) {
    const targetRect = target.getBoundingClientRect();
    dropdownLeft.value = targetRect.left - wrapperRect.left;
    dropdownMinWidth.value = targetRect.width;
  }

  await nextTick();

  const wrapperRectAfter = menuWrapperRef.value?.getBoundingClientRect();
  const dropdownRect = dropdownRef.value?.getBoundingClientRect();

  if (wrapperRectAfter && dropdownRect) {
    const maxLeft = wrapperRectAfter.width - dropdownRect.width;
    dropdownLeft.value = Math.min(dropdownLeft.value, Math.max(0, maxLeft));
  }
}

function closeDropDown() {
  hoveredItem.value = null;
}
</script>

<template>
  <nav @mouseleave="closeDropDown">
    <div
      ref="menuWrapperRef"
      class="menu-wrapper"
    >
      <div class="menu">
        <AppLink
          v-for="item in desktopMenu"
          :key="item.slug"
          :to="makeUrlRelative(item.url)"
          class="menu__item"
          @mouseenter="openDropDown(item, $event)"
        >
          <img
            v-if="item.image_url"
            :alt="item.name"
            :src="item.image_url"
            class="size-5"
          />
          <svg
            v-else-if="item.svg_icon"
            class="size-5"
            v-html="item.svg_icon"
          />
          <img
            v-else-if="item.svg_url"
            :alt="item.name"
            :src="item.svg_url"
            class="size-5"
          />
          {{ item.name }}
        </AppLink>
      </div>

      <Transition name="dropdown">
        <div
          v-if="isDropDownOpen"
          ref="dropdownRef"
          class="menu__dropdown-wrapper"
          :style="{ left: `${dropdownLeft}px`, minWidth: `${dropdownMinWidth}px` }"
        >
          <div class="menu__dropdown">
            <AppLink
              v-for="child in hoveredItem?.children"
              :key="child.slug"
              :to="makeUrlRelative(child.url)"
              class="flex items-center gap-3"
            >
              <img
                v-if="child.image_url"
                :alt="child.name"
                :src="child.image_url"
                class="size-5"
              />
              <div
                v-else-if="child.svg_icon"
                class="size-5"
                v-html="child.svg_icon"
              />
              <img
                v-else-if="child.svg_url"
                :alt="child.name"
                :src="child.svg_url"
                class="size-5"
              />
              {{ child.name }}
            </AppLink>
          </div>
        </div>
      </Transition>
    </div>
  </nav>
</template>

<style lang="scss" scoped>
.menu-wrapper {
  position: relative;
  width: 100%;
}

.menu {
  display: flex;
  justify-content: space-between;
  overflow-x: auto;
  overflow-y: hidden;
  font-size: 14px;
  -ms-overflow-style: none; /* Internet Explorer 10+ */
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none; /* Safari and Chrome */
  }

  &__item {
    display: flex;
    flex-shrink: 0;
    gap: 8px;
    align-items: center;
    padding: 12px;
  }

  &__dropdown-wrapper {
    position: absolute;
    top: 100%;
    left: 0;
    z-index: 10;
    width: max-content;
    border-top: 1px solid $black-40;
    background-color: $white;
    box-shadow: 0 8px 12px rgba($black, 0.08);
  }

  &__dropdown {
    display: flex;
    flex-direction: column;
    row-gap: 16px;
    padding: 12px;
    font-size: 14px;
  }
}

.dropdown-enter-active,
.dropdown-leave-active {
  transition: opacity 0.3s;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
}
</style>
